import { colors } from '@karnott/colors';
import { DANGER, INFO, SUCCESS, Severity, WARNING, getColorSeverity } from '@karnott/theme';

type ButtonColorMode = 'default' | 'outlined' | 'outlinedNeutral' | 'plain';
type ButtonState = 'default' | 'hovered' | 'disabled';
type ButtonColors = {
  bg: string;
  font: string;
  border: string;
};
type ButtonColorDefinition = Record<ButtonColorMode, Record<ButtonState, ButtonColors>>;

function plainColors(font: string, hoveredFont: string, disabledFont: string): Record<ButtonState, ButtonColors> {
  return {
    default: { bg: 'transparent', font, border: 'transparent' },
    hovered: { bg: 'transparent', font: hoveredFont, border: 'transparent' },
    disabled: { bg: 'transparent', font: disabledFont, border: 'transparent' },
  };
}

function severityColors(severity: Severity): ButtonColorDefinition {
  const color = getColorSeverity(severity);
  return {
    default: {
      default: { bg: colors(color), font: colors('white'), border: colors(color) },
      hovered: { bg: colors(color, 600), font: colors('white'), border: colors(color, 600) },
      disabled: { bg: colors(color, 400), font: colors('white'), border: colors(color, 400) },
    },
    outlined: {
      default: { bg: colors('white'), font: colors(color), border: colors(color) },
      hovered: { bg: colors(color), font: colors('white'), border: colors(color) },
      disabled: { bg: colors('white'), font: colors(color, 400), border: colors(color, 400) },
    },
    outlinedNeutral: {
      default: { bg: colors('white'), font: colors(color), border: colors('grey', 200) },
      hovered: { bg: colors(color), font: colors('white'), border: colors(color) },
      disabled: { bg: colors('white'), font: colors(color, 400), border: colors('grey', 200) },
    },
    plain: plainColors(colors(color), colors(color, 600), colors(color, 400)),
  };
}

export const buttonColors = {
  default: {
    default: {
      default: {
        bg: colors('white'),
        font: colors(getColorSeverity(INFO)),
        border: colors(getColorSeverity(INFO)),
      },
      hovered: {
        bg: colors(getColorSeverity(INFO), 600),
        font: colors('white'),
        border: colors(getColorSeverity(INFO), 600),
      },
      disabled: {
        bg: colors('white', 400),
        font: colors(getColorSeverity(INFO)),
        border: colors('white', 400),
      },
    },
    outlined: {
      default: {
        bg: colors('white'),
        font: colors(getColorSeverity(INFO)),
        border: colors(getColorSeverity(INFO)),
      },
      hovered: {
        bg: colors(getColorSeverity(INFO)),
        font: colors('white'),
        border: colors(getColorSeverity(INFO)),
      },
      disabled: {
        bg: colors('white'),
        font: colors(getColorSeverity(INFO), 400),
        border: colors(getColorSeverity(INFO), 400),
      },
    },
    outlinedNeutral: {
      default: {
        bg: colors('white'),
        font: colors(getColorSeverity(INFO)),
        border: colors('grey', 200),
      },
      hovered: {
        bg: colors(getColorSeverity(INFO)),
        font: colors('white'),
        border: colors(getColorSeverity(INFO)),
      },
      disabled: {
        bg: colors('white'),
        font: colors(getColorSeverity(INFO), 400),
        border: colors('grey', 200),
      },
    },
    plain: plainColors(colors('grey'), colors('black'), colors('grey')),
  },
  primary: severityColors(WARNING),
  success: severityColors(SUCCESS),
  danger: severityColors(DANGER),
  bleen: {
    default: {
      default: {
        bg: colors('bleen', 400),
        font: colors('white'),
        border: colors('bleen', 400),
      },
      hovered: {
        bg: colors('bleen', 500),
        font: colors('white'),
        border: colors('bleen', 500),
      },
      disabled: {
        bg: colors('bleen', 300),
        font: colors('white'),
        border: colors('bleen', 300),
      },
    },
    outlined: {
      default: {
        bg: colors('white'),
        font: colors('bleen', 400),
        border: colors('bleen', 400),
      },
      hovered: {
        bg: colors('bleen', 400),
        font: colors('white'),
        border: colors('bleen', 400),
      },
      disabled: {
        bg: colors('white'),
        font: colors('bleen', 300),
        border: colors('bleen', 300),
      },
    },
    outlinedNeutral: {
      default: {
        bg: 'transparent',
        font: colors('white'),
        border: colors('white'),
      },
      hovered: {
        bg: colors('white'),
        font: colors('bleen', 400),
        border: colors('white'),
      },
      disabled: {
        bg: colors('white', 400),
        font: colors('bleen', 300),
        border: colors('white', 400),
      },
    },
    plain: plainColors(colors('bleen', 500), colors('bleen', 600), colors('bleen', 400)),
  },
} satisfies Record<string, ButtonColorDefinition>;

export type ButtonMode = keyof typeof buttonColors;
